import { employees } from "../data/generated";
import type { GeneratedMail } from "../types";
import { displayDate, employeeById, senderEmail, senderName } from "../utils";
import { AttachmentList } from "./AttachmentList";

interface MailViewerProps {
  mail: GeneratedMail;
  isCurrent: boolean;
  loopCount: number;
  debug: boolean;
}

function recipientLabel(ids: string[]): string {
  return ids.map((id) => employeeById(id)?.displayName ?? id).join(", ") || "—";
}

export function MailViewer({ mail, isCurrent, loopCount, debug }: MailViewerProps) {
  const sender = employeeById(mail.senderId);
  const signatureOwner = employees.find((employee) => employee.signatureId === mail.signatureId) ?? sender;
  const signatureName = signatureOwner?.displayName ?? "不明";
  const signatureDepartment = mail.senderDepartmentOverride ?? signatureOwner?.department ?? "所属不明";
  const signatureRole = signatureOwner?.role ?? "";
  const extension = mail.signatureExtensionOverride ?? signatureOwner?.extension ?? "---";
  const flicker = loopCount >= 4 && isCurrent;

  return (
    <article className={flicker ? "mail-viewer has-flicker" : "mail-viewer"}>
      <header className="mail-header">
        <div className="mail-subject-line">
          <h2>{mail.subject}</h2>
          {!isCurrent && <span className="archive-badge">処理済み</span>}
        </div>
        <dl className="mail-metadata">
          <div><dt>From</dt><dd><strong>{senderName(mail)}</strong> &lt;{senderEmail(mail)}&gt;</dd></div>
          <div><dt>To</dt><dd>{recipientLabel(mail.toIds)}</dd></div>
          <div><dt>CC</dt><dd>{recipientLabel(mail.ccIds)}</dd></div>
          <div><dt>日時</dt><dd>{displayDate(mail)}</dd></div>
        </dl>
      </header>
      <div className="mail-body">
        {mail.body.split("\n").map((line, index) => (
          <p key={`${line}-${index}`}>{line || <>&nbsp;</>}</p>
        ))}
        <div className="signature">
          <div>――――――――――――――――――</div>
          <strong>{signatureName}</strong>
          <div>{signatureDepartment} {signatureRole}</div>
          <div>内線: {extension}</div>
          <div>{mail.signatureId === signatureOwner?.signatureId ? (signatureOwner?.email ?? "") : (sender?.email ?? "")}</div>
        </div>
        <AttachmentList attachments={mail.attachmentIds} />
      </div>
      {debug && (
        <aside className="debug-box">
          <strong>DEBUG</strong>
          <div>isAnomaly: {String(mail.isAnomaly)}</div>
          <div>anomalyId: {mail.anomalyId ?? "none"}</div>
          <div>{mail.debugExplanation ?? "正常メール"}</div>
        </aside>
      )}
    </article>
  );
}
